import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Copy, RefreshCw, Trash2, Edit, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

interface Dataset {
  id: string;
  tableName: string;
  isPublic: boolean;
  apiKey: string | null; // Full API key (if encrypted version available in backend)
  apiKeyMasked: string | null; // Masked key for identification (e.g., "abc12345...xyz9")
  accessUrl: string | null; // Direct access URL (if available)
  expiresAt: string | null;
  recordCount: number;
  createdAt: string;
  runId: string;
}

interface ApiKeyManagementProps {
  pipelineId: string;
}

export function ApiKeyManagement({ pipelineId }: ApiKeyManagementProps) {
  const [editingDataset, setEditingDataset] = useState<Dataset | null>(null);
  const [deletingDataset, setDeletingDataset] = useState<Dataset | null>(null);
  const queryClient = useQueryClient();

  // Fetch datasets
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pipelines', pipelineId, 'datasets'],
    queryFn: async () => {
      return apiClient.get<{ pipelineId: string; datasets: Dataset[] }>(
        `/pipelines/${pipelineId}/datasets`
      );
    },
    enabled: !!pipelineId,
  });

  // Update dataset mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      datasetId,
      updates,
    }: {
      datasetId: string;
      updates: { isPublic?: boolean; expiresAt?: string | null };
    }) => {
      return apiClient.put(`/pipelines/${pipelineId}/datasets/${datasetId}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines', pipelineId, 'datasets'] });
      toast.success('Dataset updated successfully');
      setEditingDataset(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update dataset');
    },
  });

  // Delete dataset mutation
  const deleteMutation = useMutation({
    mutationFn: async (datasetId: string) => {
      return apiClient.delete(`/pipelines/${pipelineId}/datasets/${datasetId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines', pipelineId, 'datasets'] });
      toast.success('Dataset revoked successfully');
      setDeletingDataset(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to revoke dataset');
    },
  });

  const handleCopyApiKey = async (apiKey: string) => {
    await navigator.clipboard.writeText(apiKey);
    toast.success('API key copied to clipboard');
  };

  const handleCopyAccessUrl = async (accessUrl: string) => {
    await navigator.clipboard.writeText(accessUrl);
    toast.success('Access URL copied to clipboard');
  };

  const handleTogglePublic = (dataset: Dataset) => {
    updateMutation.mutate({
      datasetId: dataset.id,
      updates: { isPublic: !dataset.isPublic },
    });
  };

  const handleUpdateExpiration = (datasetId: string, expiresAt: string | null) => {
    updateMutation.mutate({
      datasetId,
      updates: { expiresAt },
    });
  };

  const handleDelete = (datasetId: string) => {
    deleteMutation.mutate(datasetId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading published datasets...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
        <p className="text-sm text-destructive">Failed to load datasets. Please try again.</p>
      </div>
    );
  }

  if (!data || data.datasets.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">
          No published datasets yet. Publish your pipeline output to generate API keys.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Published Datasets</h3>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Info notice */}
      <div className="rounded-lg border border-blue-500/50 bg-blue-500/10 p-3">
        <p className="text-xs text-blue-700 dark:text-blue-400">
          ℹ️ <strong>API Access Management:</strong> You can now copy API keys and access URLs directly from this table for easy testing and integration.
        </p>
        <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
          🔗 Use the <strong>Test</strong> button to open the endpoint in a new tab, or <strong>Copy</strong> to get the URL/API key for your application.
        </p>
        <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
          ⚠️ <strong>Note:</strong> Old datasets (published before this update) only show masked keys. Republish them to enable full access management.
        </p>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Table Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>API Key</TableHead>
              <TableHead>Access URL</TableHead>
              <TableHead>Records</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.datasets.map((dataset) => (
              <TableRow key={dataset.id}>
                <TableCell className="font-medium">{dataset.tableName}</TableCell>
                <TableCell>
                  {dataset.isPublic ? (
                    <Badge variant="outline" className="bg-green-500/10 text-green-500">
                      Public
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-gray-500/10 text-gray-500">
                      Private
                    </Badge>
                  )}
                </TableCell>
                {/* API Key */}
                <TableCell>
                  <div className="flex items-center gap-2">
                    {dataset.apiKey ? (
                      <>
                        <code className="text-xs font-mono max-w-[200px] truncate" title={dataset.apiKey}>
                          {dataset.apiKey}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 flex-shrink-0"
                          onClick={() => handleCopyApiKey(dataset.apiKey!)}
                          title="Copy API key"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground" title="Old dataset - republish to enable">
                        {dataset.apiKeyMasked} (masked)
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Access URL */}
                <TableCell>
                  <div className="flex items-center gap-2">
                    {dataset.accessUrl ? (
                      <>
                        <code className="text-xs font-mono max-w-[200px] truncate" title={dataset.accessUrl}>
                          {dataset.accessUrl}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 flex-shrink-0"
                          onClick={() => handleCopyAccessUrl(dataset.accessUrl!)}
                          title="Copy access URL"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 flex-shrink-0"
                          onClick={() => window.open(dataset.accessUrl!, '_blank')}
                          title="Test endpoint in new tab"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        Not available
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{dataset.recordCount.toLocaleString()}</TableCell>
                <TableCell>
                  {dataset.expiresAt ? (
                    <span className="text-sm">
                      {format(new Date(dataset.expiresAt), 'MMM dd, yyyy')}
                    </span>
                  ) : (
                    <span className="text-sm text-muted-foreground">Never</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm">
                    {format(new Date(dataset.createdAt), 'MMM dd, yyyy')}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingDataset(dataset)}
                      title="Edit settings"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingDataset(dataset)}
                      title="Revoke access"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      {editingDataset && (
        <EditDatasetDialog
          dataset={editingDataset}
          open={!!editingDataset}
          onOpenChange={(open) => !open && setEditingDataset(null)}
          onSave={(updates) => handleUpdateExpiration(editingDataset.id, updates.expiresAt)}
          onTogglePublic={() => handleTogglePublic(editingDataset)}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingDataset} onOpenChange={(open) => !open && setDeletingDataset(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Access?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the dataset and invalidate the API key. This action
              cannot be undone.
              {deletingDataset && (
                <div className="mt-4 rounded-lg bg-muted p-3">
                  <p className="text-sm font-medium">Dataset: {deletingDataset.tableName}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {deletingDataset.recordCount.toLocaleString()} records
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingDataset && handleDelete(deletingDataset.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Revoking...' : 'Revoke Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Edit Dataset Dialog Component
interface EditDatasetDialogProps {
  dataset: Dataset;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (updates: { expiresAt: string | null }) => void;
  onTogglePublic: () => void;
  isLoading: boolean;
}

function EditDatasetDialog({
  dataset,
  open,
  onOpenChange,
  onSave,
  onTogglePublic,
  isLoading,
}: EditDatasetDialogProps) {
  const [expiresAt, setExpiresAt] = useState(
    dataset.expiresAt ? new Date(dataset.expiresAt).toISOString().split('T')[0] : ''
  );

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Dataset Settings</DialogTitle>
          <DialogDescription>Update access settings for this dataset.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Table Name</Label>
            <Input value={dataset.tableName} disabled />
          </div>

          <div className="space-y-2">
            <Label>Public Access</Label>
            <Button
              variant={dataset.isPublic ? 'default' : 'outline'}
              onClick={onTogglePublic}
              disabled={isLoading}
              className="w-full"
            >
              {dataset.isPublic ? 'Public' : 'Private'}
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expiresAt">Expiration Date</Label>
            <Input
              id="expiresAt"
              type="date"
              min={minDate}
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            {dataset.expiresAt && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpiresAt('')}
                className="text-xs"
              >
                Remove expiration
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave({ expiresAt: expiresAt || null })}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
